/**
 * Phase 101gc (Feb 2026) — Modal Slot pour le module « Tournée terrain ».
 *
 * Dialog unique avec 2 onglets Retirer / Rajouter pour un slot donné :
 *  - Retirer : liste des lots FIFO + motif (DLC/casse/blocage) + qty
 *  - Rajouter : produit (pré-rempli via mapping ou détecté) + n° lot auto
 *    (AM-YYYYMMDD-Nslot) + DLC obligatoire + qty
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, PackageMinus, PackagePlus, AlertTriangle, X, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const REASONS = [
  { code: "DLC_EXPIRED", label: "DLC échue", color: "#B93A25", sublabel: "→ retour Conthey" },
  { code: "BROKEN", label: "Barquette cassée", color: "#C77D3A", sublabel: "sortie sèche" },
  { code: "BLOCKED", label: "Bloquée machine", color: "#5B3A8C", sublabel: "sortie sèche" },
];

function fmtDLC(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-CH", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return iso;
  }
}

function badgeColor(days) {
  if (days === null || days === undefined) return { bg: "#F0F0EB", text: "#575A57" };
  if (days < 0) return { bg: "#FBE5E0", text: "#8C3A2A" };
  if (days <= 2) return { bg: "#FBEEDB", text: "#8C6B2F" };
  if (days <= 5) return { bg: "#FBF4E9", text: "#8C6B2F" };
  return { bg: "#E8F1EA", text: "#1F4E3D" };
}

export default function FieldSlotDialog({ open, onOpenChange, slot, products, distributorId, onDone }) {
  const [tab, setTab] = useState("withdraw");
  const [submitting, setSubmitting] = useState(false);

  // -------- Withdraw state --------
  const [selectedLotId, setSelectedLotId] = useState(null);
  const [wQty, setWQty] = useState(1);
  const [wReason, setWReason] = useState("DLC_EXPIRED");

  // -------- Add state --------
  const [aProductId, setAProductId] = useState(null);
  const [aQty, setAQty] = useState(1);
  const [aDlc, setADlc] = useState("");
  const [aNotes, setANotes] = useState("");
  // Phase 101gc.7 — Traçabilité lot source
  const [recentLots, setRecentLots] = useState([]);
  const [loadingRecentLots, setLoadingRecentLots] = useState(false);
  const [aSourceLotId, setASourceLotId] = useState(null); // null si "je n'ai pas de lot"
  const [aNoLotMode, setANoLotMode] = useState(false);    // toggle fallback

  const slotLots = slot?.lots || [];
  const mappedProductId = slot?.product_am?.id;

  // Phase 101gc.6 — Helper : DLC par défaut = today + shelf_life_days du
  // produit, fallback J+5 si le produit n'a pas de shelf_life renseigné.
  const computeDefaultDlc = (productId) => {
    const p = products.find((x) => x.id === productId);
    const shelfDays = Number(p?.shelf_life_days || 5) || 5;
    const d = new Date();
    d.setDate(d.getDate() + shelfDays);
    return d.toISOString().slice(0, 10);
  };

  // Reset au ré-ouverture
  useEffect(() => {
    if (open) {
      setTab(slotLots.length > 0 ? "withdraw" : "add");
      setSelectedLotId(slotLots[0]?.id || null);
      setWQty(1);
      setWReason("DLC_EXPIRED");
      setAProductId(mappedProductId || null);
      setAQty(1);
      setADlc(mappedProductId ? computeDefaultDlc(mappedProductId) : computeDefaultDlc(null));
      setANotes("");
      setASourceLotId(null);
      setANoLotMode(false);
      setRecentLots([]);
    }
  }, [open]); // eslint-disable-line

  // Phase 101gc.7 — Charge les lots récents (dépôt Conthey) du produit
  // choisi pour proposer au livreur une source traçable.
  useEffect(() => {
    if (!open || !aProductId) {
      setRecentLots([]);
      setASourceLotId(null);
      return;
    }
    let cancelled = false;
    setLoadingRecentLots(true);
    api.get(`/products/${aProductId}/recent-lots?days=7`)
      .then(r => {
        if (cancelled) return;
        const lots = r.data?.lots || [];
        setRecentLots(lots);
        // Auto-sélectionne le lot avec la DLC la plus longue par défaut
        // (livreur choisira souvent le plus frais pour maximiser la durée en machine)
        if (lots.length > 0 && !aNoLotMode) {
          const best = lots.reduce((acc, l) => {
            if (!acc) return l;
            if ((l.days_to_dlc ?? -1) > (acc.days_to_dlc ?? -1)) return l;
            return acc;
          }, null);
          if (best) {
            setASourceLotId(best.id);
            setADlc(best.dlc || computeDefaultDlc(aProductId));
          }
        } else {
          setASourceLotId(null);
        }
      })
      .catch(() => { if (!cancelled) setRecentLots([]); })
      .finally(() => { if (!cancelled) setLoadingRecentLots(false); });
    return () => { cancelled = true; };
  }, [aProductId, open, aNoLotMode]); // eslint-disable-line

  // Quand le livreur sélectionne un lot source, on aligne la DLC dessus
  useEffect(() => {
    if (!aSourceLotId) return;
    const src = recentLots.find(l => l.id === aSourceLotId);
    if (src?.dlc) setADlc(src.dlc);
  }, [aSourceLotId]); // eslint-disable-line

  const selectedLot = useMemo(
    () => slotLots.find((l) => l.id === selectedLotId) || null,
    [slotLots, selectedLotId]
  );

  const doWithdraw = async () => {
    if (!selectedLot) {
      toast.error("Sélectionne un lot d'abord");
      return;
    }
    if (wQty <= 0) {
      toast.error("Quantité invalide");
      return;
    }
    if (wQty > (selectedLot.quantity || 0)) {
      toast.error(`Max ${selectedLot.quantity} pour ce lot`);
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/lots/${selectedLot.id}/withdraw`, {
        reason: wReason,
        quantity: wQty,
      });
      const reasonLabel = REASONS.find((r) => r.code === wReason)?.label || wReason;
      toast.success(`✅ ${wQty} × retiré · ${reasonLabel}`);
      if (onDone) onDone({ op: "withdraw", qty: wQty });
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur retrait");
    } finally {
      setSubmitting(false);
    }
  };

  const doAdd = async () => {
    if (!aProductId) {
      toast.error("Sélectionne un produit");
      return;
    }
    if (aQty <= 0) {
      toast.error("Quantité invalide");
      return;
    }

    // Phase 101gc.7 — Contrôle lot source vs mode fallback
    const src = aSourceLotId ? recentLots.find(l => l.id === aSourceLotId) : null;

    if (!aNoLotMode && !src) {
      toast.error("Sélectionne un lot au dépôt, ou coche « Je n'ai pas de lot »");
      return;
    }
    if (src && aQty > src.quantity) {
      toast.error(`Ce lot ne contient que ${src.quantity} barq. au dépôt`);
      return;
    }
    if (aNoLotMode && !aDlc) {
      toast.error("DLC obligatoire quand aucun lot source");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        product_id: aProductId,
        emplacement_number: slot.slot_number,
        quantity: aQty,
        notes: aNotes,
      };
      if (src) {
        payload.source_lot_id = src.id;
      } else {
        payload.dlc = aDlc;
      }
      const r = await api.post(`/distributors/${distributorId}/field-tour/add-lot`, payload);
      const p = products.find((x) => x.id === aProductId);
      const lotLabel = r.data.lot?.lot_number || "";
      const suffix = r.data.source_split ? ` (depuis dépôt · ${lotLabel})` : ` (à rapprocher · ${lotLabel})`;
      toast.success(`✅ ${aQty} × ${p?.name || "barquettes"} ajoutées${suffix}`);
      if (onDone) onDone({ op: "add", qty: aQty });
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur ajout");
    } finally {
      setSubmitting(false);
    }
  };

  if (!slot) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg max-h-[95vh] overflow-y-auto p-0"
        data-testid="field-slot-dialog"
      >
        <DialogHeader className="px-5 pt-5">
          <DialogTitle className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center bg-[#1F4E3D] text-white rounded-md w-8 h-8 font-mono text-sm">
              {slot.slot_number}
            </span>
            <span>Slot {slot.slot_number}</span>
            {slot.mdb_code && slot.mdb_code !== String(slot.slot_number) && (
              <span className="text-xs text-[#A3A6A3] font-mono">· MDB {slot.mdb_code}</span>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Résumé produit du slot */}
        <div className="px-5 pb-3 border-b border-[#E5E5E0]">
          {slot.product_am ? (
            <>
              <div className="font-medium text-sm text-[#1A1C1A]">{slot.product_am.name}</div>
              <div className="text-xs text-[#575A57] flex flex-wrap gap-x-3 gap-y-1 mt-1">
                {slot.product_am.sku && <span className="font-mono">{slot.product_am.sku}</span>}
                <span>AM : <strong>{slot.am_stock}</strong> barq.</span>
                {slot.moma_stock !== null && slot.moma_stock !== undefined && (
                  <span>Nayax : <strong>{slot.moma_stock}</strong></span>
                )}
                {slot.dlc_shortest && (
                  <span>DLC + courte : {fmtDLC(slot.dlc_shortest)}</span>
                )}
              </div>
              {!slot.mapping_ok && (
                <div className="mt-2 text-xs text-[#8C3A2A] flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>Le mapping AM ↔ Nayax ne correspond pas. Vérifie visuellement avant d'agir.</span>
                </div>
              )}
            </>
          ) : (
            <div className="text-sm text-[#8C6B2F] flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Aucun produit mappé pour ce slot — sélectionne-en un dans « Rajouter »
            </div>
          )}
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid grid-cols-2 mx-5 mt-3">
            <TabsTrigger
              value="withdraw"
              data-testid="field-slot-tab-withdraw"
              disabled={slotLots.length === 0}
            >
              <PackageMinus className="w-4 h-4 mr-2" />
              Retirer {slotLots.length > 0 && <span className="ml-1.5 text-[10px] bg-[#F0F0EB] rounded-full px-1.5">{slotLots.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="add" data-testid="field-slot-tab-add">
              <PackagePlus className="w-4 h-4 mr-2" />
              Rajouter
            </TabsTrigger>
          </TabsList>

          {/* WITHDRAW */}
          <TabsContent value="withdraw" className="px-5 pb-5 pt-4 space-y-4" data-testid="field-slot-withdraw-panel">
            {slotLots.length === 0 ? (
              <div className="text-center py-8 text-sm text-[#A3A6A3]">
                Aucun lot dans ce slot — rien à retirer.
              </div>
            ) : (
              <>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#575A57] mb-2 block">
                    Lot à retirer (FIFO — plus courte DLC d'abord)
                  </Label>
                  <div className="space-y-2">
                    {slotLots.map((lot) => {
                      const badge = badgeColor(lot.days_to_dlc);
                      const isSelected = selectedLotId === lot.id;
                      return (
                        <button
                          key={lot.id}
                          type="button"
                          onClick={() => { setSelectedLotId(lot.id); setWQty(lot.quantity); }}
                          className={`w-full text-left border rounded-lg p-3 transition-all ${
                            isSelected
                              ? "border-[#1F4E3D] bg-[#F5F9F6] ring-1 ring-[#1F4E3D]"
                              : "border-[#E5E5E0] hover:border-[#B9D6BD]"
                          }`}
                          data-testid={`field-slot-lot-${lot.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-mono text-xs text-[#575A57]">{lot.lot_number}</div>
                              <div className="text-xs mt-1" style={{ color: badge.text }}>
                                DLC {fmtDLC(lot.dlc)}
                                {lot.days_to_dlc !== null && lot.days_to_dlc !== undefined && (
                                  <span className="ml-1">
                                    ({lot.days_to_dlc < 0 ? `${Math.abs(lot.days_to_dlc)}j dépassée` : `J-${lot.days_to_dlc}`})
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="text-2xl font-mono font-semibold text-[#1F4E3D]">×{lot.quantity}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#575A57] mb-2 block">Motif</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {REASONS.map((r) => {
                      const active = wReason === r.code;
                      return (
                        <button
                          key={r.code}
                          type="button"
                          onClick={() => setWReason(r.code)}
                          className={`px-2 py-2 rounded-md border text-xs font-medium transition-all ${
                            active ? "text-white" : "text-[#575A57] bg-white hover:bg-[#FAFAF7]"
                          }`}
                          style={{
                            backgroundColor: active ? r.color : undefined,
                            borderColor: active ? r.color : "#E5E5E0",
                          }}
                          data-testid={`field-slot-reason-${r.code}`}
                        >
                          <div>{r.label}</div>
                          <div className={`text-[9px] mt-0.5 ${active ? "text-white/80" : "text-[#A3A6A3]"}`}>{r.sublabel}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#575A57] mb-2 block">Quantité à retirer</Label>
                  <div className="flex items-center gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="w-11 h-11 text-xl"
                      onClick={() => setWQty(Math.max(1, wQty - 1))}
                      data-testid="field-slot-withdraw-minus"
                    >
                      −
                    </Button>
                    <Input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={selectedLot?.quantity || 999}
                      value={wQty}
                      onChange={(e) => setWQty(Math.max(1, Math.min(selectedLot?.quantity || 999, parseInt(e.target.value) || 1)))}
                      className="text-center text-xl h-11 flex-1"
                      data-testid="field-slot-withdraw-qty"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="w-11 h-11 text-xl"
                      onClick={() => setWQty(Math.min(selectedLot?.quantity || 999, wQty + 1))}
                      data-testid="field-slot-withdraw-plus"
                    >
                      +
                    </Button>
                  </div>
                  {selectedLot && (
                    <div className="text-xs text-[#A3A6A3] mt-1 text-right">
                      Max disponible : {selectedLot.quantity}
                    </div>
                  )}
                </div>

                <Button
                  onClick={doWithdraw}
                  disabled={submitting || !selectedLot}
                  className="w-full bg-[#8C3A2A] hover:bg-[#6E2D1F] text-white h-12 text-base"
                  data-testid="field-slot-withdraw-confirm"
                >
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <PackageMinus className="w-5 h-5 mr-2" />}
                  Retirer {wQty} barquette{wQty > 1 ? "s" : ""}
                </Button>
              </>
            )}
          </TabsContent>

          {/* ADD */}
          <TabsContent value="add" className="px-5 pb-5 pt-4 space-y-4" data-testid="field-slot-add-panel">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#575A57] mb-2 block">Produit à mettre</Label>
              <Select value={aProductId || ""} onValueChange={setAProductId}>
                <SelectTrigger className="h-11" data-testid="field-slot-add-product">
                  <SelectValue placeholder="Choisir un produit..." />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {mappedProductId && (
                    <>
                      <div className="text-[10px] uppercase tracking-wider text-[#575A57] px-2 py-1 bg-[#FBF4E9]">
                        Produit mappé pour ce slot
                      </div>
                      {(() => {
                        const p = products.find((x) => x.id === mappedProductId);
                        return p ? (
                          <SelectItem value={p.id}>
                            {p.name} {p.sku && <span className="text-[#A3A6A3] font-mono ml-1">· {p.sku}</span>}
                          </SelectItem>
                        ) : null;
                      })()}
                      <div className="text-[10px] uppercase tracking-wider text-[#575A57] px-2 py-1 bg-[#FAFAF7] mt-1">
                        Autres produits (attention au bon slot)
                      </div>
                    </>
                  )}
                  {products
                    .filter((p) => p.id !== mappedProductId)
                    .map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} {p.sku && <span className="text-[#A3A6A3] font-mono ml-1">· {p.sku}</span>}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {/* Phase 101gc.7 — Picker de lot source (dépôt Conthey) */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#575A57] mb-2 block">
                Lot au dépôt (source)
              </Label>
              {loadingRecentLots && (
                <div className="flex items-center gap-2 text-xs text-[#A3A6A3] py-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement des lots récents…
                </div>
              )}
              {!loadingRecentLots && aProductId && recentLots.length === 0 && !aNoLotMode && (
                <div className="bg-[#FBEEDB] border border-[#E8D3A8] rounded-md px-3 py-2 text-xs text-[#8C6B2F]" data-testid="field-slot-no-recent-lots">
                  Aucun lot au dépôt Conthey pour ce produit dans les 7 derniers jours.
                  Coche « Je n'ai pas de lot » ci-dessous pour ajouter quand même.
                </div>
              )}
              {!loadingRecentLots && recentLots.length > 0 && !aNoLotMode && (
                <div className="space-y-1.5 max-h-56 overflow-y-auto" data-testid="field-slot-recent-lots">
                  {recentLots.map((lot) => {
                    const isSelected = aSourceLotId === lot.id;
                    const badge = badgeColor(lot.days_to_dlc);
                    return (
                      <button
                        key={lot.id}
                        type="button"
                        onClick={() => setASourceLotId(lot.id)}
                        className={`w-full text-left border rounded-md p-2.5 transition-all flex items-center justify-between ${
                          isSelected
                            ? "border-[#1F4E3D] bg-[#F5F9F6] ring-1 ring-[#1F4E3D]"
                            : "border-[#E5E5E0] hover:border-[#B9D6BD]"
                        }`}
                        data-testid={`field-slot-source-lot-${lot.id}`}
                      >
                        <div>
                          <div className="font-mono text-sm font-semibold text-[#1A1C1A]">{lot.lot_number}</div>
                          <div className="text-[10px] mt-0.5" style={{ color: badge.text }}>
                            DLC {fmtDLC(lot.dlc)}
                            {lot.days_to_dlc !== null && lot.days_to_dlc !== undefined && (
                              <span className="ml-1">
                                ({lot.days_to_dlc < 0 ? `${Math.abs(lot.days_to_dlc)}j dépassée` : `J-${lot.days_to_dlc}`})
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xl font-mono font-bold text-[#1F4E3D] leading-none">×{lot.quantity}</div>
                          <div className="text-[9px] text-[#A3A6A3]">au dépôt</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              {/* Toggle fallback "je n'ai pas de lot" */}
              <label className="flex items-center gap-2 mt-2 cursor-pointer text-xs text-[#575A57] select-none" data-testid="field-slot-no-lot-toggle-wrap">
                <input
                  type="checkbox"
                  checked={aNoLotMode}
                  onChange={(e) => {
                    setANoLotMode(e.target.checked);
                    if (e.target.checked) setASourceLotId(null);
                  }}
                  className="w-4 h-4 accent-[#8C3A2A]"
                  data-testid="field-slot-no-lot-toggle"
                />
                <span>Je n'ai pas de lot au dépôt (marquer pour rapprochement)</span>
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#575A57] mb-2 block">Quantité</Label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="w-11 h-11 text-xl shrink-0"
                    onClick={() => setAQty(Math.max(1, aQty - 1))}
                    data-testid="field-slot-add-minus"
                  >
                    −
                  </Button>
                  <Input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    value={aQty}
                    onChange={(e) => setAQty(Math.max(1, parseInt(e.target.value) || 1))}
                    className="text-center text-xl h-11"
                    data-testid="field-slot-add-qty"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="w-11 h-11 text-xl shrink-0"
                    onClick={() => setAQty(aQty + 1)}
                    data-testid="field-slot-add-plus"
                  >
                    +
                  </Button>
                </div>
                {aSourceLotId && (() => {
                  const src = recentLots.find(l => l.id === aSourceLotId);
                  return src ? (
                    <div className="text-[10px] text-[#A3A6A3] mt-1 text-right">
                      Max disponible : {src.quantity}
                    </div>
                  ) : null;
                })()}
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-[#575A57] mb-2 block">
                  DLC
                  {aSourceLotId && (
                    <span className="text-[#1F4E3D] normal-case text-[10px] font-normal ml-1">(depuis le lot)</span>
                  )}
                  {aNoLotMode && (
                    <span className="text-[#8C6B2F] normal-case text-[10px] font-normal ml-1">(à saisir)</span>
                  )}
                </Label>
                <Input
                  type="date"
                  value={aDlc}
                  onChange={(e) => setADlc(e.target.value)}
                  disabled={!!aSourceLotId}
                  className="h-11 text-base disabled:bg-[#F5F9F6] disabled:cursor-not-allowed"
                  data-testid="field-slot-add-dlc"
                />
              </div>
            </div>

            {/* Bandeau info sur le lot final */}
            {aSourceLotId && (() => {
              const src = recentLots.find(l => l.id === aSourceLotId);
              return src ? (
                <div className="bg-[#E8F1EA] border border-[#B9D6BD] rounded-md px-3 py-2 text-[11px] text-[#1F4E3D]" data-testid="field-slot-source-info">
                  Ajout tracé depuis le lot <code className="bg-white px-1.5 py-0.5 rounded font-mono font-semibold">{src.lot_number}</code>
                  {" "}(reste {src.quantity - aQty} au dépôt après).
                </div>
              ) : null;
            })()}
            {aNoLotMode && (
              <div className="bg-[#FBEEDB] border border-[#E8D3A8] rounded-md px-3 py-2 text-[11px] text-[#8C6B2F]" data-testid="field-slot-no-lot-info">
                Ajout marqué <strong>MANUAL_NO_LOT</strong>. Alain rapprochera avec un vrai lot cuisine plus tard.
                Nouveau lot : <code className="bg-white px-1.5 py-0.5 rounded font-mono">AM-{new Date().toISOString().slice(0,10).replace(/-/g,"")}-S{slot.slot_number}</code>
              </div>
            )}

            <div>
              <Label className="text-xs uppercase tracking-wider text-[#575A57] mb-2 block">Notes (optionnel)</Label>
              <Textarea
                value={aNotes}
                onChange={(e) => setANotes(e.target.value)}
                placeholder="Ex : lot rapporté du camion en tournée"
                className="min-h-[50px]"
                data-testid="field-slot-add-notes"
              />
            </div>

            <Button
              onClick={doAdd}
              disabled={submitting || !aProductId || aQty <= 0 || (!aSourceLotId && !aNoLotMode) || (aNoLotMode && !aDlc)}
              className="w-full bg-[#1F4E3D] hover:bg-[#173B2E] text-white h-12 text-base"
              data-testid="field-slot-add-confirm"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <PackagePlus className="w-5 h-5 mr-2" />}
              Ajouter {aQty} barquette{aQty > 1 ? "s" : ""}
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
